
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import TaskActivityBox from '@/components/TaskActivityBox.vue';
import AttachmentsList from '@/components/AttachmentsList.vue';
import {
  Attachment, AttachmentInfo, AttachmentType, BoxStatusType, Lead, Task,
} from '@/types';

@Component({
  components: {
    AttachmentsList,
    TaskActivityBox,
  },
})
export default class AttachmentWithStatus extends Vue {
  @Prop(String) readonly title: string | undefined
  @Prop(String) readonly type: AttachmentType | undefined
  @Prop(String) readonly value: BoxStatusType | null | undefined;
  @Prop({ type: Boolean, default: false }) readonly loading!: boolean;
  @Prop({ type: Boolean, default: false }) readonly disabled!: boolean;
  @Prop({ type: Boolean, default: false }) readonly readonly!: boolean;
  @Prop({ type: Boolean, default: false }) readonly required!: boolean;
  @Prop({ type: Boolean, default: false }) readonly useFather!: boolean
  @Prop({ type: Boolean, default: false }) readonly isDoneDisabled!: boolean

  // Se true, è utilizzato all'interno di una Connection
  @Prop({ type: Boolean, default: false }) readonly connection!: boolean

  // Se true, è utilizzato all'interno di un Task
  @Prop(Object) readonly task: Task | undefined

  // Se specificato, consente la selezione di un allegato esistente dei tipi selezionati
  @Prop({ type: Array, default: () => [] }) readonly selectFrom!: AttachmentType[]

  // Se true, il componente emette soltanto gli eventi di aggiunta e rimozione senza chiamare le API
  @Prop({ type: Boolean, default: false }) readonly standalone!: boolean

  // Se true, il componente non mostra mai gli allegati
  @Prop({ type: Boolean, default: false }) readonly hidden!: boolean

  // Se specificato, filtra gli allegati per mostrare solo quelli della Lead con questo id
  @Prop({ type: Number, default: null }) readonly filterLeadId!: number | null

  boxStatus: BoxStatusType = 'todo';

  created() {
    console.log(`${this.constructor.name}: created()`); // eslint-disable-line no-console
    this.boxStatus = this.value ?? 'todo';
  }

  @Watch('value')
  onValue(val: BoxStatusType | null | undefined) {
    console.log(`${this.constructor.name}: onValue(${val})`); // eslint-disable-line no-console

    this.boxStatus = val ?? 'todo';
  }

  // Lo stato 'done' (Fatto) è disattivato se non ci sono allegati e il campo è obbligatorio
  get doneDisabled(): boolean {
    return this.isDoneDisabled
      || (this.noAttachments && (this.required || this.boxStatus === 'na'));
  }

  @Watch('doneDisabled')
  onDoneDisabled(val: boolean, oldVal: boolean) {
    // Se nessuna Lead è selezionata non prosegue
    if (!this.lead) return;

    console.log(`${this.constructor.name}: onDoneDisabled() ${oldVal} => ${val}`); // eslint-disable-line no-console

    // Se lo stato attuale è 'done'...
    if (this.boxStatus === 'done') {
      // ... se siamo passati da 1+ a 0 allegati...
      if (oldVal === false && val === true) {
        // ... si prepara ad aggiornare da 'done' a 'todo'
        this.boxStatus = 'todo';
        this.$emit('update', 'todo');
      }
    }
  }

  // Questo componente necessita che la Lead a cui è associato l'allegato sia selezionata
  get lead(): Lead | null {
    return this.$store.getters.currentLead as Lead | null;
  }

  get currentUpdate(): { [key: string]: never } {
    // Se utilizzato all'interno di una Connection, si utilizza l'update della Connection
    if (this.connection) return this.$store.getters.currentConnectionUpdate;

    // Se utilizzato all'interno di un Task, si utilizza l'update del Task
    if (this.task) return this.$store.getters.selectedTaskUpdate;

    // Negli altri casi si utilizza l'update della Lead
    return this.$store.getters.selectedLeadUpdate;
  }

  get currentUpdateAttachments(): AttachmentInfo[] {
    return this.currentUpdate?.attachments
      ? this.currentUpdate?.attachments as []
      : [];
  }

  get currentUpdateDeletedAttachments(): number[] {
    return this.currentUpdate?.deletedAttachments
      ? this.currentUpdate?.deletedAttachments as []
      : [];
  }

  get currentUpdateSelectedAttachments(): AttachmentInfo[] {
    return this.currentUpdate?.selectedAttachments
      ? this.currentUpdate?.selectedAttachments as []
      : [];
  }

  // Ritorna l'elenco degli id dei nuovi allegati che stiamo caricando
  get currentUpdateAttachmentsIds(): number[] {
    if (this.type) {
      return [
        ...this.currentUpdateAttachments
          .filter((a) => a.fieldname === this.type)
          .filter((a) => !this.currentUpdateDeletedAttachments.includes(a.id))
          .map((a) => a.id),
        ...this.currentUpdateSelectedAttachments
          .filter((a) => a.fieldname === this.type)
          .map((a) => a.id),
      ];
    }

    return [
      ...this.currentUpdateAttachments
        .filter((a) => !this.currentUpdateDeletedAttachments.includes(a.id))
        .map((a) => a.id),
      ...this.currentUpdateSelectedAttachments
        .map((a) => a.id),
    ];
  }

  // Tutti gli allegati associati alla Lead e alle sue eventuali figlie
  get attachments(): Attachment[] {
    if (!this.lead || !Array.isArray(this.$store.getters.currentLeadAttachments)) return [];

    let result: Attachment[] = !this.type
      ? this.$store.getters.currentLeadAttachments
      : this.$store.getters.currentLeadAttachments.filter((attachment: Attachment) => attachment.fieldname === this.type);

    if (this.filterLeadId != null) {
      result = result.filter((attachment: Attachment) => attachment.LeadId === this.filterLeadId);
    }

    return result;
  }

  // Versione dell'elenco degli allegati della Lead da cui sono rimossi
  // quelli eliminati (non confermati)
  get filteredAttachments() {
    return this.attachments.filter((a: Attachment) => {
      // Nasconde gli allegati eliminati (non confermati)
      if (this.currentUpdateDeletedAttachments.includes(a.id)) return false;
      return true;
    });
  }

  // Ritorna il conteggio degli allegati, includendo anche i nuovi non ancora memorizzati
  get attachmentsCount(): number {
    return this.filteredAttachments.length + this.currentUpdateAttachmentsIds.length;
  }

  get noAttachments(): boolean { return this.attachmentsCount === 0; }

  get boxColor(): string {
    switch (this.boxStatus) {
      case 'todo':
        return 'grey';

      case 'done':
        return 'success';

      case 'waiting':
        return 'orange';

      case 'na':
        return 'grey';

      default:
        break;
    }

    return '';
  }

  get boxDark(): boolean {
    switch (this.boxStatus) {
      case 'todo':
      case 'done':
      case 'waiting':
        return true;

      case 'na':
        return false;

      default:
        break;
    }

    return true;
  }

  get boxOutlined(): boolean {
    switch (this.boxStatus) {
      case 'todo':
      case 'done':
      case 'waiting':
        return false;

      case 'na':
        return true;

      default:
        break;
    }

    return false;
  }

  get boxText(): string {
    switch (this.boxStatus) {
      case 'todo':
        return 'Da fare';

      case 'done':
        return 'Fatto';

      case 'waiting':
        return 'In attesa';

      case 'na':
        return 'Non applicabile';

      default:
        break;
    }

    return 'Non disponibile';
  }

  async setBoxStatus(status: BoxStatusType) {
    if (this.boxStatus === status) {
      console.log(`${this.constructor.name}: setBoxStatus(${status}) (non modificato, ignorato)`); // eslint-disable-line no-console
      return;
    }

    console.log(`${this.constructor.name}: setBoxStatus(${status})`); // eslint-disable-line no-console

    this.boxStatus = status;

    this.$emit('update', status);
  }
}
